// Director task text: dates and times in the viewer's zone WITH the zone label, due and silence
// phrases, initials and category names. Pure functions over the active i18n `t` (its `language`
// picks the locale); `timeZone` omitted means the viewer's own zone. "Today"/"Tomorrow" are decided
// on the DISPLAY zone's calendar, never UTC's.
import type { I18nInstance } from '@affine/i18n';

import type {
  DirectorCadence,
  DirectorRepeatPattern,
  DirectorTask,
} from '../../../../../modules/erp/director-tasks-types';

const SHAPES = {
  dateTime: {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  },
  time: { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' },
  date: { weekday: 'short', day: 'numeric', month: 'short' },
  calendarDay: { year: 'numeric', month: 'numeric', day: 'numeric' },
  wallClock: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  },
  weekday: { weekday: 'long' },
  weekdayShort: { weekday: 'short' },
  monthShort: { month: 'short' },
} satisfies Record<string, Intl.DateTimeFormatOptions>;
type Shape = keyof typeof SHAPES;

/** The calendar-day key is locale-independent: Gregorian, Latin digits. */
const CALENDAR_LOCALE = 'en-US-u-ca-gregory-nu-latn';
const HOUR_MS = 3_600_000;

// Module-level cache: constructing an Intl formatter per call is the expensive part, and these run
// per row per render.
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(
  locale: string | undefined,
  timeZone: string | undefined,
  shape: Shape
): Intl.DateTimeFormat {
  const key = `${locale ?? ''}|${timeZone ?? ''}|${shape}`;
  let cached = formatters.get(key);
  if (!cached) {
    try {
      cached = new Intl.DateTimeFormat(locale, { ...SHAPES[shape], timeZone });
    } catch {
      // An unknown zone (or locale) from the server must never break the screen: fall back to
      // the viewer's own zone, which still carries its label.
      cached = new Intl.DateTimeFormat(undefined, SHAPES[shape]);
    }
    formatters.set(key, cached);
  }
  return cached;
}

const localeOf = (t: I18nInstance) => t.language || undefined;

function parse(iso: string): Date | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function numericParts(
  date: Date,
  timeZone: string | undefined,
  shape: 'calendarDay' | 'wallClock'
): Record<string, number> {
  return formatter(CALENDAR_LOCALE, timeZone, shape)
    .formatToParts(date)
    .reduce<Record<string, number>>((acc, part) => {
      acc[part.type] = Number(part.value);
      return acc;
    }, {});
}

/** Days since the epoch of the date's calendar day in `timeZone` (DST-safe: compares dates). */
function dayNumber(date: Date, timeZone: string | undefined): number {
  const parts = numericParts(date, timeZone, 'calendarDay');
  return Date.UTC(parts.year, parts.month - 1, parts.day) / (24 * HOUR_MS);
}

/** The zone's wall clock at `instant`, read as if it were UTC (minute precision). */
function wallClockMs(instant: number, timeZone: string): number {
  const p = numericParts(new Date(instant), timeZone, 'wallClock');
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
}

const pad = (n: number) => String(n).padStart(2, '0');
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;

/** A wall-clock date (`YYYY-MM-DD`) and time (`HH:MM`) in `timeZone` as a UTC RFC 3339 instant:
 *  ("2026-10-02", "17:00", Chicago) → "2026-10-02T22:00:00Z". The offset is the one in force at
 *  that moment, so it follows daylight saving; a time the clocks skip still yields an instant an
 *  hour away. `null` for malformed input. */
export function zonedToUtcIso(
  date: string,
  time: string,
  timeZone: string
): string | null {
  const d = DATE_RE.exec(date);
  const tm = TIME_RE.exec(time);
  if (!d || !tm) return null;
  const [y, mo, day, h, mi] = [d[1], d[2], d[3], tm[1], tm[2]].map(Number);
  const wall = Date.UTC(y, mo - 1, day, h, mi);
  const check = new Date(wall);
  if (
    check.getUTCMonth() !== mo - 1 ||
    check.getUTCDate() !== day ||
    h > 23 ||
    mi > 59
  )
    return null;
  // Guess with the offset at the wall time read as UTC, then correct once with the offset at the
  // guessed instant (they differ only within hours of a daylight-saving change).
  const guess = wall - (wallClockMs(wall, timeZone) - wall);
  const instant = wall - (wallClockMs(guess, timeZone) - guess);
  return toInstantIso(new Date(instant));
}

/** An instant as the wire carries it: UTC RFC 3339, whole seconds without a fraction
 *  ("2026-10-02T22:00:00Z"). */
export function toInstantIso(date: Date): string {
  return date.toISOString().replace('.000Z', 'Z');
}

/** The calendar day `days` after `date` (`YYYY-MM-DD`, any zone's calendar), with its weekday
 *  (0 = Sunday … 6). */
export function addCalendarDays(
  date: string,
  days: number
): { date: string; weekday: number } {
  const [y, m, d] = date.split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d + days));
  return {
    date: day.toISOString().slice(0, 10),
    weekday: day.getUTCDay(),
  };
}

/** A UTC instant as the wall-clock date and time it shows in `timeZone`. */
export function utcToZoned(
  iso: string,
  timeZone: string
): { date: string; time: string } | null {
  const date = parse(iso);
  if (!date) return null;
  const p = numericParts(date, timeZone, 'wallClock');
  return {
    date: `${p.year}-${pad(p.month)}-${pad(p.day)}`,
    time: `${pad(p.hour)}:${pad(p.minute)}`,
  };
}

/** The calendar day (`YYYY-MM-DD`) `instant` falls on in `timeZone`; `''` for an unknown zone. */
export function calendarDayIn(instant: Date, timeZone: string): string {
  return utcToZoned(instant.toISOString(), timeZone)?.date ?? '';
}

/** The weekday (0 = Sunday … 6) of `date` on the calendar of `timeZone`. */
export function weekdayIn(date: Date, timeZone: string): number {
  // The epoch day, 1970-01-01, was a Thursday.
  return (((dayNumber(date, timeZone) + 4) % 7) + 7) % 7;
}

/** "Monday" for 1 (0 = Sunday), in the active language. */
export function weekdayName(index: number, t: I18nInstance): string {
  // 2026-10-04 was a Sunday.
  return formatter(localeOf(t), 'UTC', 'weekday').format(
    new Date(Date.UTC(2026, 9, 4 + index))
  );
}

/** "Weekdays", or "Every Tuesday" for a weekly pattern (0 = Sunday). */
export function repeatText(
  pattern: DirectorRepeatPattern,
  weekday: number | null,
  t: I18nInstance
): string {
  return pattern === 'weekly' && weekday !== null
    ? t['com.affine.pipe.director.tasks.recurring.repeats.weekly']({
        weekday: weekdayName(weekday, t),
      })
    : t['com.affine.pipe.director.tasks.recurring.repeats.weekdays']();
}

/** A task's repeat chip: its pattern, the generic "Repeats" when only the recurrence id is known,
 *  or `null` when the task does not repeat. */
export function taskRepeatText(
  task: Pick<DirectorTask, 'recurrenceId' | 'repeat' | 'repeatWeekday'>,
  t: I18nInstance
): string | null {
  if (task.repeat) return repeatText(task.repeat, task.repeatWeekday, t);
  return task.recurrenceId
    ? t['com.affine.pipe.director.tasks.repeats']()
    : null;
}

/** "Fri, Oct 2, 5:30 PM CDT" (wording and order follow the active language). */
export function formatDateTime(
  iso: string,
  t: I18nInstance,
  timeZone?: string
): string {
  const date = parse(iso);
  return date ? formatter(localeOf(t), timeZone, 'dateTime').format(date) : iso;
}

/** "5:30 PM CDT". */
export function formatTime(
  iso: string,
  t: I18nInstance,
  timeZone?: string
): string {
  const date = parse(iso);
  return date ? formatter(localeOf(t), timeZone, 'time').format(date) : iso;
}

const WALL_TIME_RE = /^(\d{1,2}):(\d{2})/;
const wallFormatters = new Map<string, Intl.DateTimeFormat>();
/** The language's own clock style (12- or 24-hour), read in UTC so a wall time never shifts. */
function wallFormatter(t: I18nInstance): Intl.DateTimeFormat {
  const locale = t.language || '';
  let format = wallFormatters.get(locale);
  if (!format) {
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
    } as const;
    try {
      format = new Intl.DateTimeFormat(locale || undefined, options);
    } catch {
      format = new Intl.DateTimeFormat(undefined, options);
    }
    wallFormatters.set(locale, format);
  }
  return format;
}
const wallDate = (hour: number, minute: number) =>
  new Date(Date.UTC(2026, 0, 1, hour, minute));

/** A wall-clock time ("17:30") in the language's 12/24-hour style — "5:30 PM" or "17:30" — with no
 *  zone label: it is the clock of the person it belongs to (an update time is the assignee's, a
 *  repeat time the assignee's), never shifted into the viewer's zone. */
export function wallTime(time: string, t: I18nInstance): string {
  const match = WALL_TIME_RE.exec(time);
  if (!match) return time;
  return wallFormatter(t).format(wallDate(Number(match[1]), Number(match[2])));
}

/** The language's clock: `null` for a 24-hour clock, else its two day-period words
 *  (`["AM", "PM"]` in English) — what a typed time needs beside its hour. */
export function dayPeriods(t: I18nInstance): [string, string] | null {
  const format = wallFormatter(t);
  const cycle = format.resolvedOptions().hourCycle;
  if (cycle !== 'h11' && cycle !== 'h12') return null;
  const period = (hour: number) =>
    format.formatToParts(wallDate(hour, 0)).find(p => p.type === 'dayPeriod')
      ?.value ?? '';
  const [am, pm] = [period(9), period(21)];
  return am && pm && am !== pm ? [am, pm] : null;
}

/** "Fri, Oct 2" for a calendar day (`YYYY-MM-DD`), in the active language; the input as given
 *  when malformed. */
export function formatCalendarDate(date: string, t: I18nInstance): string {
  const match = DATE_RE.exec(date);
  if (!match) return date;
  const [y, m, d] = match.slice(1).map(Number);
  return formatter(localeOf(t), 'UTC', 'date').format(
    new Date(Date.UTC(y, m - 1, d))
  );
}

/** The month calendar's captions in the active language: weekday names from Sunday and month
 *  names from January, each list joined by commas. */
export function calendarCaptions(t: I18nInstance): {
  weekDays: string;
  monthNames: string;
} {
  const weekday = formatter(localeOf(t), 'UTC', 'weekdayShort');
  const month = formatter(localeOf(t), 'UTC', 'monthShort');
  const strip = (s: string) => s.replaceAll(',', '');
  return {
    // 2026-10-04 was a Sunday.
    weekDays: [0, 1, 2, 3, 4, 5, 6]
      .map(i => strip(weekday.format(new Date(Date.UTC(2026, 9, 4 + i)))))
      .join(','),
    monthNames: Array.from({ length: 12 }, (_, i) =>
      strip(month.format(new Date(Date.UTC(2026, i, 15))))
    ).join(','),
  };
}

/** "Fri, Oct 2" — a calendar day, no time. */
function formatDate(date: Date, t: I18nInstance, timeZone?: string) {
  return formatter(localeOf(t), timeZone, 'date').format(date);
}

/** "Today 3:00 PM CDT" | "Tomorrow 9:00 AM CDT" | "Yesterday …" | "Fri, Oct 9". `inline` gives
 *  the form that sits inside a sentence ("due today 3:00 PM CDT"); each language owns its casing. */
export function formatDay(
  iso: string,
  now: Date,
  t: I18nInstance,
  timeZone?: string,
  inline = false
): string {
  const date = parse(iso);
  if (!date) return iso;
  const time = formatter(localeOf(t), timeZone, 'time').format(date);
  switch (dayNumber(date, timeZone) - dayNumber(now, timeZone)) {
    case 0:
      return inline
        ? t['com.affine.pipe.director.tasks.day.today-inline']({ time })
        : t['com.affine.pipe.director.tasks.day.today']({ time });
    case 1:
      return inline
        ? t['com.affine.pipe.director.tasks.day.tomorrow-inline']({ time })
        : t['com.affine.pipe.director.tasks.day.tomorrow']({ time });
    case -1:
      return inline
        ? t['com.affine.pipe.director.tasks.day.yesterday-inline']({ time })
        : t['com.affine.pipe.director.tasks.day.yesterday']({ time });
    default:
      return formatDate(date, t, timeZone);
  }
}

/** "just now" | "12m ago" | "5h ago" | "2d ago". A future instant (clock skew) reads "just now". */
export function relativeShort(iso: string, now: Date, t: I18nInstance): string {
  const date = parse(iso);
  if (!date) return iso;
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return t['com.affine.pipe.director.tasks.relative.now']();
  if (minutes < 60)
    return t['com.affine.pipe.director.tasks.relative.minutes']({
      n: String(minutes),
    });
  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return t['com.affine.pipe.director.tasks.relative.hours']({
      n: String(hours),
    });
  return t['com.affine.pipe.director.tasks.relative.days']({
    n: String(Math.floor(hours / 24)),
  });
}

export type DueTone = 'late' | 'normal' | 'done';

/** The due column: "2d late" / "Today 3:00 PM CDT" / "Done 10:40 AM CDT" / "—". Lateness comes
 *  from the server's `overdue` flag; only the size of it is measured here. */
export function dueText(
  task: DirectorTask,
  now: Date,
  t: I18nInstance,
  timeZone?: string
): { text: string; tone: DueTone } {
  if (task.status === 'done' || task.status === 'cancelled') {
    const done = task.status === 'done';
    const closed = task.closedAt ? parse(task.closedAt) : null;
    if (!closed)
      return {
        text: done
          ? t['com.affine.pipe.director.tasks.due.done-undated']()
          : t['com.affine.pipe.director.tasks.due.cancelled-undated'](),
        tone: 'done',
      };
    const when =
      dayNumber(closed, timeZone) === dayNumber(now, timeZone)
        ? formatter(localeOf(t), timeZone, 'time').format(closed)
        : formatDate(closed, t, timeZone);
    return {
      text: done
        ? t['com.affine.pipe.director.tasks.due.done']({ when })
        : t['com.affine.pipe.director.tasks.due.cancelled']({ when }),
      tone: 'done',
    };
  }
  const due = task.dueAt ? parse(task.dueAt) : null;
  if (!due)
    return {
      text: t['com.affine.pipe.director.tasks.none'](),
      tone: task.overdue ? 'late' : 'normal',
    };
  if (task.overdue) {
    const hours = Math.max(
      1,
      Math.floor((now.getTime() - due.getTime()) / HOUR_MS)
    );
    return {
      text:
        hours < 24
          ? t['com.affine.pipe.director.tasks.due.late-hours']({
              n: String(hours),
            })
          : t['com.affine.pipe.director.tasks.due.late-days']({
              n: String(Math.floor(hours / 24)),
            }),
      tone: 'late',
    };
  }
  return { text: formatDay(task.dueAt!, now, t, timeZone), tone: 'normal' };
}

export function cadenceLabel(cadence: DirectorCadence, t: I18nInstance) {
  switch (cadence) {
    case 'daily':
      return t['com.affine.pipe.director.tasks.cadence.daily']();
    case 'every2Days':
      return t['com.affine.pipe.director.tasks.cadence.every2Days']();
    case 'weekly':
      return t['com.affine.pipe.director.tasks.cadence.weekly']();
  }
}

/** "Daily by 5:30 PM": the cadence and the assignee's own update time, bare (no zone label — the
 *  time is on the assignee's clock, whoever reads it). Only the cadence when the time is unreadable. */
export function updateExpectedText(
  task: Pick<DirectorTask, 'updateCadence' | 'updateDueTime'>,
  t: I18nInstance
): string {
  const cadence = cadenceLabel(task.updateCadence, t);
  return WALL_TIME_RE.test(task.updateDueTime)
    ? t['com.affine.pipe.director.tasks.mine.next.by']({
        cadence,
        time: wallTime(task.updateDueTime, t),
      })
    : cadence;
}

/** The cadence inside a sentence: "updates daily" | "updates every 2 days" | "updates weekly". */
export function cadenceUpdatesPhrase(
  cadence: DirectorCadence,
  t: I18nInstance
) {
  switch (cadence) {
    case 'daily':
      return t['com.affine.pipe.director.tasks.cadence.updates.daily']();
    case 'every2Days':
      return t['com.affine.pipe.director.tasks.cadence.updates.every2Days']();
    case 'weekly':
      return t['com.affine.pipe.director.tasks.cadence.updates.weekly']();
  }
}

export type SilenceTone = 'silent' | 'fresh' | 'closed';

/** The cadence chip: "Daily · silent 52h" / "Daily · not opened" / "Daily · 5h ago" / "Closed".
 *  Silence is the server's verdict (`stale` + `silentHours`); it is never recomputed here. */
export function silenceText(
  task: DirectorTask,
  now: Date,
  t: I18nInstance
): { text: string; tone: SilenceTone } {
  if (task.status === 'done' || task.status === 'cancelled')
    return {
      text: t['com.affine.pipe.director.tasks.silence.closed'](),
      tone: 'closed',
    };
  const cadence = cadenceLabel(task.updateCadence, t);
  if (task.stale)
    return {
      text: t['com.affine.pipe.director.tasks.silence.silent']({
        cadence,
        hours: String(task.silentHours),
      }),
      tone: 'silent',
    };
  if (task.status === 'notAccepted')
    return {
      text: t['com.affine.pipe.director.tasks.silence.not-opened']({
        cadence,
      }),
      tone: 'silent',
    };
  const since = task.lastUpdateAt ?? task.acceptedAt ?? task.createdAt;
  return {
    text: t['com.affine.pipe.director.tasks.silence.last']({
      cadence,
      when: relativeShort(since, now, t),
    }),
    tone: 'fresh',
  };
}

/** "Alex Rivera" → "AR"; one word → its first letter; nothing → "?". */
export function initials(name: string | null): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const letter = (word: string) => Array.from(word)[0].toLocaleUpperCase();
  return words.length === 1
    ? letter(words[0])
    : letter(words[0]) + letter(words[words.length - 1]);
}

/** The parser's four suggested categories and `other` are translated; any other slug (categories are free
 *  slugs on the wire) is shown as readable words. */
export function categoryLabel(slug: string, t: I18nInstance): string {
  switch (slug) {
    case 'vendor_compliance':
      return t['com.affine.pipe.director.tasks.category.vendor_compliance']();
    case 'equipment_procurement':
      return t[
        'com.affine.pipe.director.tasks.category.equipment_procurement'
      ]();
    case 'supply_sourcing':
      return t['com.affine.pipe.director.tasks.category.supply_sourcing']();
    case 'bd_outreach':
      return t['com.affine.pipe.director.tasks.category.bd_outreach']();
    case 'other':
      return t['com.affine.pipe.director.tasks.category.other']();
    default: {
      const words = slug.replaceAll('_', ' ');
      return words.charAt(0).toLocaleUpperCase() + words.slice(1);
    }
  }
}

const sizeFormatters = new Map<string, Intl.NumberFormat>();

/** A file size in the viewer's locale: "820 byte", "14 kB", "3.2 MB" — counted in 1,024s, as the
 *  25 MB limit is. */
export function formatFileSize(bytes: number, t: I18nInstance): string {
  const [unit, value] =
    bytes >= 1024 * 1024
      ? (['megabyte', bytes / (1024 * 1024)] as const)
      : bytes >= 1024
        ? (['kilobyte', bytes / 1024] as const)
        : (['byte', bytes] as const);
  const locale = localeOf(t);
  const key = `${locale ?? ''}|${unit}`;
  let cached = sizeFormatters.get(key);
  if (!cached) {
    const options = {
      style: 'unit',
      unit,
      maximumFractionDigits: 1,
    } satisfies Intl.NumberFormatOptions;
    try {
      cached = new Intl.NumberFormat(locale, options);
    } catch {
      cached = new Intl.NumberFormat(undefined, options);
    }
    sizeFormatters.set(key, cached);
  }
  return cached.format(value);
}
